import React, { useEffect, useRef, useState } from "react";
import { Lock, ShieldCheck, X, Loader2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useTokenPurchase } from "@/hooks/useTokenPurchase";
import { trackEvent } from "@/utils/analytics";
import { translateApiError } from "@/utils/apiErrors";
import api from "@/lib/api";

// Fetched once (per page load) and shared across every mount of this component - the
// profile id never changes, no need to re-fetch it for every pack the user selects.
let profilePromise = null;
function getMollieProfile() {
  if (!profilePromise) {
    profilePromise = api.get("/payments/mollie-profile-id").then(({ data }) => data).catch((e) => {
      profilePromise = null;
      throw e;
    });
  }
  return profilePromise;
}

// mollie.js is now loaded with `defer` in index.html (Core Web Vitals fix, Itération 58) so it
// no longer blocks first paint on every page - but that means `window.Mollie` isn't guaranteed
// to already exist the instant this component mounts (especially now that the card form shows
// immediately on page load, itération 57, rather than after a pack click). Poll briefly instead
// of silently giving up, so the form never fails to render just because of a loading race.
function waitForMollieJs(timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    if (window.Mollie) return resolve();
    const start = Date.now();
    const interval = setInterval(() => {
      if (window.Mollie) {
        clearInterval(interval);
        resolve();
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(interval);
        reject(new Error("mollie.js failed to load in time"));
      }
    }, 100);
  });
}

const CARD_ICON_BASE = "https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons@0.1.1/flat-rounded";
const CARD_BRANDS = ["visa", "mastercard", "amex"];

function canUseApplePayWeb() {
  try {
    return typeof window !== "undefined" && !!window.ApplePaySession && window.ApplePaySession.canMakePayments();
  } catch {
    return false;
  }
}

// No official lightweight "is Google Pay ready" check exists without loading Google's full
// Pay.js SDK (which itself needs real merchant/gateway config just to answer yes/no). Google
// Pay on the web only ever works on Chromium browsers and never on iOS, so that heuristic is
// used here instead - it never produces a false positive for the two platforms that actually
// caused the Aug 12 "Expiré" cluster (Apple Pay button shown on non-Apple, Google Pay shown
// on iOS), without adding any new dependency.
function canUseGooglePayWeb() {
  try {
    if (typeof window === "undefined" || !window.PaymentRequest) return false;
    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    if (isIOS) return false;
    return /Chrome|Chromium|Edg|OPR/.test(ua);
  } catch {
    return false;
  }
}

// Mollie Components render inside sandboxed iframes (no CSS inheritance from our page), so
// the light/white look has to be told to Mollie explicitly via this `styles` option. Text
// stays the same neutral dark color in every state (no green "valid" tint - looked like a
// phishing/scam page to users, kept only red for genuine errors).
const FIELD_STYLES = {
  base: {
    color: "#111827",
    fontSize: "15px",
    fontFamily: "inherit",
    "::placeholder": { color: "#9ca3af" },
  },
  valid: { color: "#111827" },
  invalid: { color: "#dc2626" },
};

function FieldBox({ inputRef, label, testId, focused, setFocused, trailing }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label className="block text-[11px] font-medium text-gray-500">{label}</label>
        {trailing}
      </div>
      <div
        ref={inputRef}
        onFocus={() => setFocused?.(true)}
        onBlur={() => setFocused?.(false)}
        className={`h-11 rounded-xl border bg-gray-50 px-3 py-2.5 transition-all ${
          focused ? "border-primary ring-2 ring-primary/20 bg-white" : "border-gray-200"
        }`}
        data-testid={testId}
      />
    </div>
  );
}

/** Premium "we accept" chips (Apple.com style: white rounded chip + soft shadow per brand)
 * shown right next to the card number field itself, always visible. */
function CardBrandBadges() {
  return (
    <div className="flex items-center gap-1" data-testid="mollie-card-brand-badges">
      {CARD_BRANDS.map((brand) => (
        <span
          key={brand}
          className="flex h-6 w-9 items-center justify-center rounded-md border border-black/5 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
        >
          <img src={`${CARD_ICON_BASE}/${brand}.svg`} alt={brand} className="h-3.5 w-auto" />
        </span>
      ))}
    </div>
  );
}

/**
 * Payment panel shown inline once a token pack is selected - never a separate page. Two
 * tabs: "Carte bancaire" (Mollie Components, embedded card fields, Apple/Amazon-style light
 * card) and "Google Pay / Apple Pay" (native wallet button, still routed through Mollie but
 * scoped to that single wallet method so its hosted page shows only that pay button).
 */
export default function MollieCardPayment({ pack, resumeOrderId, resumeAction, onSuccess, onCancel, subscriptionPlan = null, songPurchase = false }) {
  const { language } = useI18n();
  const { buyPackWithCard, buyPackWithMethod } = useTokenPurchase();
  const [tab, setTab] = useState("card");
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [walletLoading, setWalletLoading] = useState(null);
  // Apple Pay (13/08/2026) - confirmé fonctionnel par un vrai appel à l'API Mollie :
  // `method: "applepay"` (sans token) retourne une URL de paiement hébergée valide et 100%
  // DÉDIÉE (`https://www.mollie.com/checkout/wallets/apple-pay/...`, aucun champ de carte visible)
  // - le "Hosted Checkout" suffit, seule la détection de compatibilité navigateur manquait
  // (itération 52).
  //
  // Google Pay reste désactivé, décision explicite de l'utilisateur (itération 59, 13/08/2026) :
  // Mollie n'a PAS de valeur "googlepay" dans son enum `method` (contrairement à "applepay") -
  // Google Pay n'apparaît que sur la page hébergée générique "Cards" (method="creditcard"), qui
  // affiche AUSSI les champs de saisie manuelle de carte (déjà présents sur notre propre page) en
  // plus du bouton Google Pay - impossible à masquer côté Mollie sans repasser par l'intégration
  // "directe" complète (SDK Google + Merchant ID approuvé), écartée par l'utilisateur car trop
  // complexe. Redirection déjà techniquement fonctionnelle (backend traduit "googlepay" en
  // "creditcard", voir payments.py) mais jugée source de confusion (doublon de champs carte sur
  // une page qu'on ne maîtrise pas) - le bouton reste donc caché. Repasser à
  // `useState(canUseGooglePayWeb)` si l'utilisateur change d'avis ou qu'une vraie intégration
  // directe est un jour construite.
  const [canApplePay] = useState(canUseApplePayWeb);
  const canGooglePay = false;
  const hasWallet = canApplePay || canGooglePay;

  const cardNumberRef = useRef(null);
  const cardHolderRef = useRef(null);
  const expiryDateRef = useRef(null);
  const verificationCodeRef = useRef(null);
  const mollieRef = useRef(null);
  const componentsRef = useRef([]);

  const isEn = language === "en";

  // Funnel visibility (Itération 49 audit): fires once, right when this payment panel becomes
  // visible - i.e. the visitor has picked a token pack and is now looking at the actual
  // payment step, regardless of which tab (card/wallet) they end up using.
  useEffect(() => {
    trackEvent("begin_checkout", { value: subscriptionPlan?.price_eur ?? pack, currency: "EUR" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab !== "card") return;
    let cancelled = false;
    getMollieProfile()
      .then(async ({ profile_id, test_mode }) => {
        await waitForMollieJs();
        if (cancelled) return null;
        return { profile_id, test_mode };
      })
      .then((profile) => {
        if (cancelled || !profile) return;
        const { profile_id, test_mode } = profile;
        const mollie = window.Mollie(profile_id, {
          locale: isEn ? "en_US" : "fr_FR",
          testmode: !!test_mode,
        });
        mollieRef.current = mollie;
        // Mollie.js v1 has no single "all-in-one" component - 4 separate fields must be
        // mounted (cardNumber, cardHolder, expiryDate, verificationCode) before createToken()
        // will accept the form, even though we group them into one visual card here.
        const fields = [
          ["cardHolder", cardHolderRef.current],
          ["cardNumber", cardNumberRef.current],
          ["expiryDate", expiryDateRef.current],
          ["verificationCode", verificationCodeRef.current],
        ];
        componentsRef.current = fields.map(([type, el]) => {
          const component = mollie.createComponent(type, { styles: FIELD_STYLES });
          component.mount(el);
          return component;
        });
        setReady(true);
      })
      .catch(() => setError(isEn ? "Could not load the payment form." : "Impossible de charger le formulaire de paiement."));
    return () => {
      cancelled = true;
      componentsRef.current.forEach((c) => c?.unmount?.());
      componentsRef.current = [];
      setReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pack, tab]);

  const handleCardSubmit = async (e) => {
    e.preventDefault();
    if (!mollieRef.current) return;
    setProcessing(true);
    setError("");
    try {
      const { token, error: tokenError } = await mollieRef.current.createToken();
      if (tokenError) {
        setError(tokenError.message || (isEn ? "Invalid card details." : "Détails de carte invalides."));
        trackEvent("payment_error", { provider: "mollie", method: "card", error_name: "invalid_card_token" });
        return;
      }
      trackEvent("add_payment_info", { value: subscriptionPlan?.price_eur ?? pack, currency: "EUR", method: "card" });
      // Subscription mode: the card token becomes Mollie's "first" payment (month 1 + mandate
      // for automatic monthly renewals) - token pack mode is completely untouched. An active
      // promo code discounts the first month only; renewals always run at full plan price.
      const data = subscriptionPlan
        ? (await api.post("/subscriptions/create", { plan_id: subscriptionPlan.plan_id, platform: "web", card_token: token, resume_order_id: resumeOrderId, promo_code: subscriptionPlan.promo_code || null })).data
        : await buyPackWithCard(pack, token, { resumeOrderId, resumeAction, songPurchase });
      if (data.checkout_url) {
        // 3-D Secure challenge requested by the bank - unavoidable, brief redirect.
        window.location.href = data.checkout_url;
        return;
      }
      onSuccess(data);
    } catch (err) {
      setError(translateApiError(err?.response?.data?.detail, language) || (isEn ? "Payment failed, please try again." : "Le paiement a échoué, veuillez réessayer."));
      trackEvent("payment_error", { provider: "mollie", method: "card", error_name: err?.response?.status || "unknown" });
    } finally {
      setProcessing(false);
    }
  };

  const handleWalletPay = async (method) => {
    setWalletLoading(method);
    setError("");
    trackEvent("add_payment_info", { value: pack, currency: "EUR", method });
    try {
      const data = await buyPackWithMethod(pack, method, { resumeOrderId, resumeAction });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      onSuccess(data);
    } catch (err) {
      setError(translateApiError(err?.response?.data?.detail, language) || (isEn ? "Payment failed, please try again." : "Le paiement a échoué, veuillez réessayer."));
      trackEvent("payment_error", { provider: "mollie", method, error_name: err?.response?.status || "unknown" });
      setWalletLoading(null);
    }
  };

  return (
    <div
      className="mt-4 overflow-hidden rounded-3xl bg-white text-gray-900 shadow-2xl shadow-black/50 ring-1 ring-black/5 animate-in fade-in slide-in-from-top-2"
      data-testid="mollie-card-form"
    >
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <p className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <Lock className="h-3.5 w-3.5 text-emerald-600" />
          {subscriptionPlan
            ? (() => {
                const eur = (v) => (isEn ? `$${v.toFixed(2)}` : `${v.toFixed(2).replace(".", ",")}€`);
                const hasPromo = subscriptionPlan.first_price_eur != null && subscriptionPlan.first_price_eur !== subscriptionPlan.price_eur;
                if (hasPromo) {
                  return isEn
                    ? `Subscribe — ${eur(subscriptionPlan.first_price_eur)} first month, then ${eur(subscriptionPlan.price_eur)}/month`
                    : `S'abonner — ${eur(subscriptionPlan.first_price_eur)} le 1er mois, puis ${eur(subscriptionPlan.price_eur)}/mois`;
                }
                return isEn
                  ? `Subscribe — ${eur(subscriptionPlan.price_eur)}/month`
                  : `S'abonner — ${eur(subscriptionPlan.price_eur)}/mois`;
              })()
            : (isEn ? `Pay $${pack} securely` : `Payer ${String(pack).replace(".", ",")}€ en toute sécurité`)}
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          data-testid="mollie-card-cancel-btn"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="px-5 py-5">
        {/* Payment method tabs - the wallet tab (and buttons inside it) only ever appears when
            at least one of Apple Pay / Google Pay is genuinely usable on this device/browser,
            so nobody is ever routed to a dead-end Mollie page (root cause of the Aug 12 cluster
            of "Expiré" payments - both buttons used to show to 100% of visitors regardless of
            platform support). */}
        {hasWallet && (
          <div className="mb-4 grid grid-cols-2 gap-2 rounded-2xl bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => setTab("card")}
              className={`rounded-xl py-2 text-sm font-medium transition-all ${
                tab === "card" ? "bg-white text-gray-900 shadow" : "text-gray-500"
              }`}
              data-testid="mollie-tab-card"
            >
              {isEn ? "Card" : "Carte bancaire"}
            </button>
            <button
              type="button"
              onClick={() => setTab("wallet")}
              className={`rounded-xl py-2 text-sm font-medium transition-all ${
                tab === "wallet" ? "bg-white text-gray-900 shadow" : "text-gray-500"
              }`}
              data-testid="mollie-tab-wallet"
            >
              Google Pay / Apple Pay
            </button>
          </div>
        )}

        {(!hasWallet || tab === "card") ? (
          <form onSubmit={handleCardSubmit} className="space-y-3">
            <FieldBox
              inputRef={cardHolderRef}
              label={isEn ? "Cardholder name" : "Titulaire de la carte"}
              testId="mollie-field-holder"
              focused={focusedField === "holder"}
              setFocused={(v) => setFocusedField(v ? "holder" : null)}
            />
            <FieldBox
              inputRef={cardNumberRef}
              label={isEn ? "Card number" : "Numéro de carte"}
              testId="mollie-field-number"
              focused={focusedField === "number"}
              setFocused={(v) => setFocusedField(v ? "number" : null)}
              trailing={<CardBrandBadges />}
            />
            <div className="flex gap-2">
              <div className="flex-1">
                <FieldBox
                  inputRef={expiryDateRef}
                  label={isEn ? "Expiry date" : "Date d'expiration"}
                  testId="mollie-field-expiry"
                  focused={focusedField === "expiry"}
                  setFocused={(v) => setFocusedField(v ? "expiry" : null)}
                />
              </div>
              <div className="flex-1">
                <FieldBox
                  inputRef={verificationCodeRef}
                  label="CVC"
                  testId="mollie-field-cvc"
                  focused={focusedField === "cvc"}
                  setFocused={(v) => setFocusedField(v ? "cvc" : null)}
                />
              </div>
            </div>

            {!ready && !error && (
              <p className="flex items-center gap-1.5 text-xs text-gray-400">
                <Loader2 className="h-3 w-3 animate-spin" />
                {isEn ? "Loading secure payment form..." : "Chargement du formulaire sécurisé..."}
              </p>
            )}
            {error && (
              <p className="text-sm font-medium text-red-600" data-testid="mollie-card-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={!ready || processing}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gray-900 py-3 text-sm font-semibold text-white transition-all hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="mollie-card-submit-btn"
            >
              {processing && <Loader2 className="h-4 w-4 animate-spin" />}
              {processing
                ? isEn ? "Processing..." : "Traitement..."
                : subscriptionPlan
                  ? (() => {
                      const eur = (v) => (isEn ? `$${v.toFixed(2)}` : `${v.toFixed(2).replace(".", ",")}€`);
                      const hasPromo = subscriptionPlan.first_price_eur != null && subscriptionPlan.first_price_eur !== subscriptionPlan.price_eur;
                      if (hasPromo) {
                        return isEn
                          ? `Pay ${eur(subscriptionPlan.first_price_eur)} (1st month)`
                          : `Payer ${eur(subscriptionPlan.first_price_eur)} (1er mois)`;
                      }
                      return isEn ? `Subscribe — ${eur(subscriptionPlan.price_eur)}/month` : `S'abonner — ${eur(subscriptionPlan.price_eur)}/mois`;
                    })()
                  : isEn ? `Pay $${pack}` : `Payer ${String(pack).replace(".", ",")}€`}
            </button>
          </form>
        ) : (
          <div className="space-y-2.5">
            {canApplePay && (
              <button
                type="button"
                onClick={() => handleWalletPay("applepay")}
                disabled={!!walletLoading}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-black py-3 text-sm font-semibold text-white transition-all hover:bg-gray-800 disabled:opacity-60"
                data-testid="mollie-wallet-applepay-btn"
              >
                {walletLoading === "applepay" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.47-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8.87.06 1.72-.63 3.07-.78 1.7-.18 3.11.66 4.05 2.02-3.56 2.18-2.98 6.86.65 8.32-.5 1.28-1.15 2.55-1.85 3.61ZM12.03 7.25c-.15-2.23 1.66-4.09 3.75-4.25.29 2.31-2.09 4.05-3.75 4.25Z"/></svg>
                )}
                {walletLoading === "applepay" ? (isEn ? "Redirecting..." : "Redirection...") : "Apple Pay"}
              </button>
            )}
            {canGooglePay && (
              <button
                type="button"
                onClick={() => handleWalletPay("googlepay")}
                disabled={!!walletLoading}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-3 text-sm font-semibold text-gray-800 transition-all hover:bg-gray-50 disabled:opacity-60"
                data-testid="mollie-wallet-googlepay-btn"
              >
                {walletLoading === "googlepay" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <svg viewBox="0 0 24 24" className="h-4 w-4">
                    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.44 3.58v2.97h3.86c2.26-2.09 3.6-5.17 3.6-8.79Z"/>
                    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.94l-3.86-2.97c-1.07.72-2.45 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.07C3.26 21.3 7.31 24 12 24Z"/>
                    <path fill="#FBBC05" d="M5.27 14.28A7.19 7.19 0 0 1 4.9 12c0-.79.14-1.56.37-2.28V6.65H1.29A11.96 11.96 0 0 0 0 12c0 1.93.46 3.76 1.29 5.35l3.98-3.07Z"/>
                    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.94 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.65l3.98 3.07C6.22 6.86 8.87 4.75 12 4.75Z"/>
                  </svg>
                )}
                {walletLoading === "googlepay" ? (isEn ? "Redirecting..." : "Redirection...") : "Google Pay"}
              </button>
            )}
            {error && (
              <p className="text-sm font-medium text-red-600" data-testid="mollie-wallet-error">
                {error}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-1.5 border-t border-gray-100 bg-gray-50 px-5 py-3 text-[11px] text-gray-500">
        <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
        {isEn ? "Encrypted payments, secured by Mollie" : "Paiements chiffrés et sécurisés par Mollie"}
      </div>
    </div>
  );
}

